// Lib
import React, { useMemo } from 'react';
import classNames from 'classnames';

// Utils
import { getBackgroundColorClass } from '../utils/dataUtils/cell/tableCellStyleUtils';

// Components
import CellCheckboxRenderer from '../cellTypes/CellCheckboxRenderer';
import DraftCellRendererComponent from './DraftCellRendererComponent';
import CellFormulaErrorRenderer from './CellFormulaErrorRenderer';
import TiptapCellRendererComponent from './TiptapCellRendererComponent';

// Hooks
import useMilanoteCellRendererFormulaInfo from './useMilanoteCellRendererFormulaInfo';

// Constants
import { CellTypeConstants, CellTypeNames } from '../../../../common/table/CellTypeConstants';

// Types
import { CellData, HotComputedCellValue, TableDataVersion } from '../../../../common/table/TableTypes';

import type { MNHotInstance } from '../tableHotInstanceTypes';
import type { DetailedCellError } from 'hyperformula';

import './MilanoteCellRendererComponent.scss';
import './MilanoteCellComponent.scss';

type Props = {
    elementId: string;
    row: number;
    col: number;
    hotComputedCellValue: HotComputedCellValue;
    cellData?: CellData;
    hotTableInstance: MNHotInstance;
    version: TableDataVersion;
    hide?: boolean;
    filterQuery?: string;
    locale: string;
};

const MilanoteCellRendererComponent: React.FC<Props> = (props) => {
    const {
        elementId,
        row,
        col,
        hotComputedCellValue,
        cellData,
        hotTableInstance,
        version,
        hide,
        locale,
        filterQuery,
    } = props;

    const cellFormulaInfo = useMilanoteCellRendererFormulaInfo(props, [hotComputedCellValue, cellData, locale]);

    const backgroundClasses = useMemo(
        () => cellData && getBackgroundColorClass(cellData),
        [cellData, cellData?.background],
    );
    const className = classNames('MilanoteCellRendererComponent', backgroundClasses);

    if (!cellData) return null;
    if (hide) return null;

    if (cellFormulaInfo?.isFormulaError) {
        return (
            <div className={className}>
                <CellFormulaErrorRenderer cellError={cellFormulaInfo.hfCellValue as DetailedCellError} />
            </div>
        );
    }

    if (cellData.type?.name === CellTypeNames.CHECKBOX) {
        const alignment = cellData.textAlignment || CellTypeConstants[cellData.type.name].alignment;

        return (
            <div className={classNames(className, 'button-cell-renderer', `align-${alignment}`)}>
                <CellCheckboxRenderer
                    row={row}
                    col={col}
                    hotComputedCellValue={hotComputedCellValue}
                    isEditable={!cellFormulaInfo?.isFormula}
                    hotTableInstance={hotTableInstance}
                />
            </div>
        );
    }

    const RendererComponent =
        version === TableDataVersion.V3 ? TiptapCellRendererComponent : DraftCellRendererComponent;

    return (
        <div className={className}>
            <RendererComponent
                elementId={elementId}
                hotComputedCellValue={hotComputedCellValue}
                cellData={cellData}
                locale={locale}
                filterQuery={filterQuery}
                cellFormulaInfo={cellFormulaInfo}
            />
        </div>
    );
};

export default MilanoteCellRendererComponent;
